import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import { ChartSpec, debounce, isSp, renderChart } from "src/site/recharts";

// Site::PrefectureArea::LastThreeYearsRent::Component の Stimulus コントローラ。
// landIeul: RentChartByLastThreeYearsRent (PC) / RentChartByLastThreeYearsRentSp (SP) の
// recharts LineChart を再現する。useIsSp による DOM の入れ替えも再現する。

interface SeriesValue {
  key: string;
  name: string;
  stroke: string;
}

const TICKS_PC_OVER20 = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const TICKS_PC = [0, 5, 10, 15, 20, 25];
const TICKS_SP_OVER20 = [0, 5, 10, 15, 20, 30, 35, 40, 45, 50];
const TICKS_SP = [0, 5, 10, 15, 20, 25];

class SitePrefectureAreaLastThreeYearsRentController extends Controller<HTMLElement> {
  static targets = ["headingPc", "headingSp", "graphPc", "graphSp"];
  static values = { rows: Array, series: Array, over20: Boolean };

  declare readonly headingPcTarget: HTMLElement;
  declare readonly headingSpTarget: HTMLElement;
  declare readonly graphPcTarget: HTMLElement;
  declare readonly graphSpTarget: HTMLElement;
  declare readonly rowsValue: Array<Record<string, number>>;
  declare readonly seriesValue: SeriesValue[];
  declare readonly over20Value: boolean;

  private onResize?: () => void;

  connect(): void {
    this.draw();
    this.onResize = debounce(() => this.draw());
    window.addEventListener("resize", this.onResize);
  }

  disconnect(): void {
    if (this.onResize) window.removeEventListener("resize", this.onResize);
  }

  private draw(): void {
    const sp = isSp();
    this.headingPcTarget.hidden = sp;
    this.headingSpTarget.hidden = !sp;
    this.graphPcTarget.hidden = sp;
    this.graphSpTarget.hidden = !sp;
    if (sp) {
      this.graphPcTarget.innerHTML = "";
      renderChart(this.graphSpTarget, this.spec(true));
      this.appendYAxisUnit();
    } else {
      this.graphSpTarget.innerHTML = "";
      renderChart(this.graphPcTarget, this.spec(false));
    }
  }

  private spec(sp: boolean): ChartSpec {
    const series = this.seriesValue.map((s) => ({
      key: s.key,
      name: s.name,
      stroke: s.stroke,
      fill: s.stroke,
      strokeWidth: sp ? 2 : 3,
    }));
    if (sp) {
      return {
        kind: "line",
        data: this.rowsValue,
        xKey: "leaseDate",
        xPadding: { left: 8, right: 8 },
        xTickFontSize: "10px",
        yTickFontSize: "10px",
        yTicks: this.over20Value ? TICKS_SP_OVER20 : TICKS_SP,
        margin: { top: 0, right: 20, bottom: 0, left: 0 },
        curve: "linear",
        legendWrapperStyle: {
          width: "100%",
          padding: "0px 20px",
          "line-height": "40px",
        },
        tooltipFontSize: "11px",
        emitDashArray: true,
        series,
        containerStyle: "width: 120%; height: 90%; min-width: 0px;",
      };
    }
    return {
      kind: "line",
      data: this.rowsValue,
      xKey: "leaseDate",
      xPadding: { left: 20, right: 20 },
      xTickFontSize: "15px",
      yTickFontSize: "15px",
      yTicks: this.over20Value ? TICKS_PC_OVER20 : TICKS_PC,
      yUnit: "万円",
      curve: "linear",
      legendWrapperStyle: {
        "font-size": "15px",
        "margin-left": "20px",
        "line-height": "40px",
      },
      tooltipFontSize: "16px",
      emitDashArray: true,
      series,
      containerStyle: "width:100%;height:100%;min-width:0",
    };
  }

  // landIeul: RentChartByLastThreeYearsRentSp#YAxisItemSet。
  // SP の YAxis は unit を持たないため、描画後に "0" の目盛りだけ「万円」を足している。
  private appendYAxisUnit(): void {
    this.graphSpTarget
      .querySelectorAll(".recharts-text tspan")
      .forEach((tspan) => {
        if (tspan.textContent === "0") tspan.append("万円");
      });
  }
}

application.register(
  "site--prefecture-area--last-three-years-rent",
  SitePrefectureAreaLastThreeYearsRentController,
);
